if (!customElements.get('matrix-color-picker')) {
  class MatrixColorPicker extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {
        debug: false,
      };

      // Get translation
      this.translations = parseJson(this.dataset.translations);

      // Selectors
      this.form = this.querySelector('form');
      this.inputs = this.querySelectorAll('input');
      this.submitButton = this.querySelector('[type="submit"]');
      this.closeButton = this.querySelector('[data-close-drawer]');

      // Configurator selectors
      this.colorButton = document.querySelector('[data-color-button]');
      this.colorPlaceholder = document.querySelector('[data-color-placeholder]');
      if (!this.colorButton || !this.colorPlaceholder) {
        return;
      }
      this.colorImage = this?.colorPlaceholder.querySelector('[data-color-placeholder-image]');
      this.colorTitle = this?.colorPlaceholder.querySelector('[data-color-placeholder-title]');

      // Event handlers
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.querySelectorAll('input').forEach((input) => {
        input.addEventListener('change', this.onChange.bind(this));
      });
    }

    /**
     * On change input
     * @param {object} event
     */
    onChange(event) {
      this.inputs.forEach((input) => {
        input.closest('[data-option]').classList.remove('active');
      });
      const input = event.currentTarget || event.target;
      input.closest('[data-option]').classList.add('active');
      this.toggleSubmitButton(true);
    }

    /**
     * On submit form
     * @param {object} event
     */
    onSubmit(event) {
      event.preventDefault();

      // Get selected option data
      this.formData = new FormData(this.form);
      this.id = this.formData.get('option_id');
      this.input = this.querySelector(`input[value="${this.id}"]`);
      this.colorName = this.input.dataset?.optionColorName;
      this.quality = this.input.dataset?.optionQuality;
      this.title = this.input.dataset?.optionTitle;
      this.sku = this.input.dataset?.optionSku;
      this.image = this.input.dataset.optionImage || '';

      // Update configurator inputs
      this.colorPlaceholder.querySelector('input[name="option_id"]').value = this.id;
      this.colorPlaceholder.querySelector('input[name="option_title"]').value = this.title;
      this.colorPlaceholder.querySelector('input[name="option_color_name"]').value = this.colorName;
      this.colorPlaceholder.querySelector('input[name="option_sku"]').value = this.sku;

      // Trigger change event on configurator inputs
      this.colorPlaceholder
        .querySelector('input[name="option_id"]')
        .dispatchEvent(new Event('change'));

      // Update color option image
      if (this.image) {
        this.colorImage.innerHTML = `<img src="${this.image}" width="48" height="48" loading="lazy" alt="${this.title}" class="aspect-square w-14 overflow-hidden rounded-lg object-cover">`;
      } else {
        this.colorImage.innerHTML = '';
      }

      // Update color option title
      if (this.colorName) {
        this.colorTitle.innerHTML = `${this.title} - ${this.colorName}`;
      } else {
        this.colorTitle.innerHTML = this.title;
      }

      // Toggle option color placeholder/button
      this.toggleColorPlaceholder(true);

      // Close the option color drawer
      this.closeButton.dispatchEvent(new Event('click'));
    }

    /**
     * Toggle submit button
     * @param {boolean} active
     */
    toggleSubmitButton(active = true) {
      if (active) {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.innerHTML = this.translations.submit_button_text;
        this.submitButton.setAttribute('aria-label', this.translations.submit_button_text);
      } else {
        this.submitButton.setAttribute('disabled', true);
        this.submitButton.innerHTML = this.translations.submit_button_text_disabled;
        this.submitButton.setAttribute('aria-label', this.translations.submit_button_text_disabled);
      }
    }

    /**
     * Toggle option drawer placeholder/button
     * @param {boolean} active (true a option drawer is selected, false no option drawer is selected)
     */
    toggleColorPlaceholder(active = true) {
      if (!this.colorButton || !this.colorPlaceholder) {
        return;
      }

      if (active) {
        this.colorPlaceholder.classList.remove('pointer-events-none');
        this.colorPlaceholder.classList.remove('overflow-hidden');
        this.colorPlaceholder.classList.remove('opacity-0');
        this.colorPlaceholder.style.maxHeight = '5rem';
        this.colorButton.classList.add('pointer-events-none');
        this.colorButton.classList.add('overflow-hidden');
        this.colorButton.classList.add('opacity-0');
        this.colorButton.style.maxHeight = '0';
      } else {
        this.colorPlaceholder.classList.add('pointer-events-none');
        this.colorPlaceholder.classList.add('overflow-hidden');
        this.colorPlaceholder.classList.add('opacity-0');
        this.colorPlaceholder.style.maxHeight = '0';
        this.colorButton.classList.remove('pointer-events-none');
        this.colorButton.classList.remove('overflow-hidden');
        this.colorButton.classList.remove('opacity-0');
        this.colorButton.style.maxHeight = '5rem';
      }
    }

    /**
     * Filter color options based on option type
     * @param {string} type - translucent / obscuring
     */
    filter(type) {
      if (!type) {
        return;
      }
      let firstInput = false;
      this.inputs.forEach((input) => {
        const inputType = input.dataset?.optionType;
        if (inputType == type) {
          input.closest('[data-option]').classList.remove('hidden');

          // Make the first option visible in the price calculator
          // only set the id so we know the step is not completed yet
          if (!firstInput) {
            firstInput = input;
            this.colorPlaceholder.querySelector('input[name="option_id"]').value = input.value;
            this.colorPlaceholder.querySelector('input[name="option_title"]').value = '';
            this.colorPlaceholder.querySelector('input[name="option_color_name"]').value = '';
            this.colorPlaceholder.querySelector('input[name="option_sku"]').value = '';
            this.colorPlaceholder
              .querySelector('input[name="option_id"]')
              .dispatchEvent(new Event('change'));
          }
        } else {
          input.closest('[data-option]').classList.add('hidden');
        }
      });
    }

    /**
     * Clear color options picker
     */
    clear() {
      this.toggleColorPlaceholder(false);

      // Clear inputs in drawer
      this.inputs.forEach((input) => {
        input.closest('[data-option]').classList.remove('active');
        input.checked = false;
      });

      // Reset submit in drawer
      this.toggleSubmitButton(false);

      // Clear inputs in configurator
      this.colorImage.innerHTML = '';
      this.colorTitle.innerHTML = '';
    }
  }

  window.MatrixColorPicker = MatrixColorPicker;

  customElements.define('matrix-color-picker', MatrixColorPicker);
}
